import {
    Federation,
    OAUTH_FEDERATION_SESSION_KEY,
} from "./logoutSession";

const PKCE_VERIFIER_KEY = "wings_oauth_pkce_verifier";
const PKCE_STATE_KEY = "wings_oauth_pkce_state";

function randomUrlSafe(bytes = 32): string {
    const arr = new Uint8Array(bytes);
    crypto.getRandomValues(arr);
    return base64UrlEncode(arr);
}

function base64UrlEncode(data: ArrayBuffer | Uint8Array): string {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]!);
    }
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sha256Base64Url(plain: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(plain));
    return base64UrlEncode(digest);
}

function clientIdFor(federation: Federation): string {
    return federation === "apple"
        ? import.meta.env.VITE_COGNITO_CLIENTID_APPLE
        : import.meta.env.VITE_COGNITO_CLIENTID;
}

export function clearPkceSession(): void {
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_STATE_KEY);
}

/**
 * Inicia SSO via Cognito Hosted UI com Authorization Code + PKCE
 * (mesmo contrato do app mobile). Google recebe `prompt=select_account`.
 */
export async function startSsoAuthorization(federation: Federation): Promise<void> {
    const clientId = clientIdFor(federation);
    const cognitoDomain = import.meta.env.VITE_COGNITO_USERPOOLURL;
    const redirectUri = import.meta.env.VITE_COGNITO_REDIRECTURI;

    const verifier = randomUrlSafe(32);
    const challenge = await sha256Base64Url(verifier);
    const state = randomUrlSafe(16);

    sessionStorage.setItem(OAUTH_FEDERATION_SESSION_KEY, federation);
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(PKCE_STATE_KEY, state);

    const params = new URLSearchParams({
        response_type: "code",
        client_id: clientId,
        redirect_uri: redirectUri,
        scope: "openid email profile",
        code_challenge_method: "S256",
        code_challenge: challenge,
        state,
    });

    if (federation === "google") {
        params.set("identity_provider", "Google");
        params.set("prompt", "select_account");
    } else {
        params.set("identity_provider", "Apple");
        params.set("prompt", "login");
    }

    window.location.assign(`${cognitoDomain}/oauth2/authorize?${params.toString()}`);
}

export type OAuthTokenSet = {
    idToken: string;
    accessToken: string;
    refreshToken: string;
};

/**
 * Se a URL atual for callback `?code=...`, troca o code por tokens e limpa a query.
 * Retorna null quando não há callback PKCE pendente.
 */
export async function consumeAuthorizationCodeCallback(): Promise<OAuthTokenSet | null> {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    const error = url.searchParams.get("error");

    if (error) {
        clearPkceSession();
        throw new Error(url.searchParams.get("error_description") || error);
    }

    if (!code) {
        return null;
    }

    const expectedState = sessionStorage.getItem(PKCE_STATE_KEY);
    if (!state || !expectedState || state !== expectedState) {
        clearPkceSession();
        throw new Error("Estado OAuth inválido. Tente o login novamente.");
    }

    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (!verifier) {
        clearPkceSession();
        throw new Error("Sessão PKCE expirada. Tente o login novamente.");
    }

    const pending = sessionStorage.getItem(OAUTH_FEDERATION_SESSION_KEY);
    const federation: Federation = pending === "apple" ? "apple" : "google";
    const clientId = clientIdFor(federation);
    const cognitoDomain = import.meta.env.VITE_COGNITO_USERPOOLURL;
    const redirectUri = import.meta.env.VITE_COGNITO_REDIRECTURI;

    const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
        code_verifier: verifier,
    });

    const res = await fetch(`${cognitoDomain}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
    });

    clearPkceSession();

    if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(errText || `Falha na troca do código OAuth (${res.status})`);
    }

    const json = (await res.json()) as {
        id_token?: string;
        access_token?: string;
        refresh_token?: string;
    };

    if (!json.id_token || !json.refresh_token) {
        throw new Error("Resposta OAuth incompleta (id_token/refresh_token).");
    }

    url.searchParams.delete("code");
    url.searchParams.delete("state");
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

    return {
        idToken: json.id_token,
        accessToken: json.access_token ?? "",
        refreshToken: json.refresh_token,
    };
}
